import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { paletteColor } from "../lib/palette";
import {
  CheckCircle2,
  Clock3,
  Download,
  FileText,
  Flag,
  MessageCircle,
  RotateCcw,
  StickyNote,
  Trash2,
  X,
} from "lucide-react";
import { useStore } from "../lib/store";
import type { Lead, Priority, SavedView } from "../lib/types";
import {
  CLIENT_VIEWS,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PERIOD,
  HEAT_BANDS,
  HEAT_EXPLANATION,
  LEAD_VIEWS,
  LOCKED_COLUMNS,
  PAGE_SIZES,
  PERIOD_OPTIONS,
  TRASH_VIEWS,
  buildRows,
  columnsFor,
  csvFileName,
  deltaLabel,
  filterRows,
  growth,
  heatColor,
  matchesView,
  orderColumns,
  pageCount,
  paginate,
  periodRange,
  priorityLabel,
  rangeLabel,
  scopeRows,
  sortRows,
  sourceColor,
  summarize,
  type HeatBandId,
  type LeadRow,
  type ListScope,
  type PeriodId,
  type SortDirection,
  type SortKey,
  type ViewId,
} from "../features/leads/list";
import {
  daysInFunnel,
  situation,
  stageJourney,
  timeline,
  type TimelineTone,
} from "../features/leads/profile";
import { NewTaskDialog } from "../features/tasks/NewTaskDialog";
import { useModalOverlay } from "../components/overlay";
import { Dialog } from "../ui/Dialog";
import { Button } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import { ConfirmDialog } from "./PerfilDialogs";
import { showFeedback } from "../lib/feedback";
import { Field, TextInput } from "../ui/Field";
import { HelpPopover, type MetricExplanation } from "../ui/HelpPopover";
import { CompactSelect } from "../ui/CompactSelect";
import { CustomPeriod, PricePopup, money } from "./lista-popovers";
import { avatarGradient, avatarInitials } from "../ui/avatar-identity";
import { appPath } from "../app/routes";
import { ExportDialog } from "./ExportDialog";
import "./lista.css";

/**
 * Leads, Clientes e Lixeira — a página do protótipo Zênite, sobre o store.
 *
 * Um só componente porque as três superfícies têm a mesma composição, os mesmos
 * filtros e as mesmas colunas; o que muda é a base e o conjunto de ações. Isso
 * evita três formulários divergentes do mesmo cadastro (AGENTS §19.1).
 *
 * Toda a seleção mora na URL: visão, busca, funil, faixa de AI Score, período,
 * dias sem interação, faixa de valor, ordenação, página e tamanho. Recarregar,
 * voltar de um perfil ou compartilhar o endereço recompõe a mesma lista (§3).
 *
 * O indicador e a lista saem da mesma chamada de `filterRows`, então o número
 * ao lado da visão é sempre o número de linhas que ela mostra (§10).
 */

const SCOPE_META: Record<
  ListScope,
  { title: string; noun: string; nounPlural: string; path: string; searchLabel: string }
> = {
  leads: {
    title: "Leads",
    noun: "lead",
    nounPlural: "leads",
    path: "leads",
    searchLabel: "Buscar leads por nome, empresa ou tag",
  },
  clientes: {
    title: "Clientes",
    noun: "cliente",
    nounPlural: "clientes",
    path: "clientes",
    searchLabel: "Buscar clientes por nome, empresa ou tag",
  },
  lixeira: {
    title: "Lixeira de leads",
    noun: "lead",
    nounPlural: "leads na lixeira",
    path: "lixeira",
    searchLabel: "Buscar na lixeira por nome, empresa ou tag",
  },
};

const QUIET_OPTIONS = [
  { value: "0", label: "Qualquer período" },
  { value: "7", label: "7 dias ou mais" },
  { value: "14", label: "14 dias ou mais" },
  { value: "30", label: "30 dias ou mais" },
];

const REMOVAL_OPTIONS = [
  { value: "0", label: "Qualquer data" },
  { value: "7", label: "Últimos 7 dias" },
  { value: "30", label: "Últimos 30 dias" },
  { value: "90", label: "Últimos 90 dias" },
];

const DEFAULT_SORT: Record<ListScope, { key: SortKey; direction: SortDirection }> = {
  leads: { key: "value", direction: "desc" },
  clientes: { key: "value", direction: "desc" },
  lixeira: { key: "removed", direction: "desc" },
};

/* Ícones do protótipo para as visões (traços de 1.6). */
const VIEW_ICONS: Record<string, ReactNode> = {
  todos: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M4 10h16M10 10v10" />
    </>
  ),
  quentes: <path d="m13 3-8 11h6l-1 7 9-12h-6z" />,
  esfriando: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  "sem-tarefa": (
    <>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4V2m6 2V2M9 12h6" />
    </>
  ),
  perdidos: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="m8 12 3 3 5-6" />
    </>
  ),
  lixeira: <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />,
  salva: (
    <>
      <path d="M4 5h16M7 12h10M10 19h4" />
    </>
  ),
};

function NavIcon({ id }: { id: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {VIEW_ICONS[id] ?? VIEW_ICONS.todos}
    </svg>
  );
}

function priorityClass(priority: Priority): string {
  return priority === "high" ? "alta" : priority === "medium" ? "media" : "baixa";
}

function quietLabel(row: LeadRow): string {
  if (!row.lead.lastInteractionAt) return "Sem interação";
  if (row.quietDays === 0) return "hoje";
  if (row.quietDays === 1) return "ontem";
  return `há ${row.quietDays} dias`;
}

function viewsFor(scope: ListScope): ReadonlyArray<{ id: string; label: string }> {
  if (scope === "lixeira") return TRASH_VIEWS;
  if (scope === "clientes") return CLIENT_VIEWS;
  return LEAD_VIEWS;
}

function isPriority(value: string | null): value is Priority {
  return value === "low" || value === "medium" || value === "high";
}

function toNumber(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number(value.replace(/[^\d]/g, ""));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function isCivilDay(value: string | null): value is string {
  return Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value));
}

function periodPhrase(period: PeriodId): string {
  switch (period) {
    case "hoje":
      return "hoje";
    case "semana":
      return "nos últimos 7 dias";
    case "mes":
      return "nos últimos 30 dias";
    case "tri":
      return "no trimestre";
    case "custom":
      return "no período escolhido";
    default:
      return "em todo o período";
  }
}

export function LeadsPage() {
  return <ListaPage scope="leads" />;
}

export function ClientesPage() {
  return <ListaPage scope="clientes" />;
}

export function LixeiraPage() {
  return <ListaPage scope="lixeira" />;
}

function ListaPage({ scope }: { scope: ListScope }) {
  const store = useStore();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [now] = useState(() => new Date().toISOString());
  const titleRef = useRef<HTMLHeadingElement>(null);

  const [peekId, setPeekId] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [savingView, setSavingView] = useState(false);
  const [removendoVisao, setRemovendoVisao] = useState<SavedView | null>(null);
  const [removing, setRemoving] = useState<Lead | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [priceOpen, setPriceOpen] = useState(false);
  const priceButtonRef = useRef<HTMLButtonElement>(null);

  const meta = SCOPE_META[scope];
  const views = viewsFor(scope);

  useEffect(() => {
    titleRef.current?.focus();
  }, [scope]);

  const update = useCallback(
    (patch: Record<string, string | null>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          // Qualquer mudança de recorte volta para a primeira página: manter a
          // página antiga mostraria uma seleção vazia sem explicação.
          if (!("pagina" in patch)) next.delete("pagina");
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const rawView = params.get("visao");
  const view = (views.some((item) => item.id === rawView) ? rawView : views[0].id) as ViewId;
  const query = params.get("q") ?? "";
  const funnelId = params.get("funil");
  const priority = isPriority(params.get("prioridade")) ? (params.get("prioridade") as Priority) : null;
  const rawHeat = params.get("calor");
  const heatBand = (HEAT_BANDS.some((band) => band.id === rawHeat) ? rawHeat : "todos") as HeatBandId;
  const rawPeriod = params.get("periodo");
  // A lixeira não recorta por data: o domínio não registra a remoção.
  const period: PeriodId =
    scope === "lixeira"
      ? "todos"
      : ((PERIOD_OPTIONS.some((item) => item.id === rawPeriod) ? rawPeriod : DEFAULT_PERIOD) as PeriodId);
  const from = isCivilDay(params.get("de")) ? params.get("de") : null;
  const to = isCivilDay(params.get("ate")) ? params.get("ate") : null;
  const quietDays = QUIET_OPTIONS.some((item) => item.value === params.get("quieto"))
    ? Number(params.get("quieto"))
    : 0;
  const removedWithinDays =
    scope === "lixeira" && REMOVAL_OPTIONS.some((item) => item.value === params.get("remocao"))
      ? Number(params.get("remocao"))
      : 0;
  const minValue = toNumber(params.get("min"));
  const maxValue = toNumber(params.get("max"));
  const sortKey = (params.get("ordem") as SortKey | null) ?? DEFAULT_SORT[scope].key;
  const sortDirection = (
    params.get("dir") === "asc" ? "asc" : params.get("dir") === "desc" ? "desc" : DEFAULT_SORT[scope].direction
  ) as SortDirection;
  const pageSize = Number(params.get("tamanho")) || DEFAULT_PAGE_SIZE;
  const requestedPage = Number(params.get("pagina")) || 1;

  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    columnsFor(scope).map((column) => column.id),
  );

  const allRows = useMemo(
    () =>
      buildRows({
        leads: store.leads,
        memberships: store.memberships,
        stages: store.stages,
        funnels: store.funnels,
        tags: store.tags,
        tasks: store.tasks,
        leadFields: store.leadFields,
        leadFieldValues: store.leadFieldValues,
        now,
      }),
    [
      now,
      store.funnels,
      store.leadFields,
      store.leadFieldValues,
      store.leads,
      store.memberships,
      store.stages,
      store.tags,
      store.tasks,
    ],
  );

  const base = useMemo(() => scopeRows(allRows, scope), [allRows, scope]);
  const leadsBase = useMemo(() => scopeRows(allRows, "leads"), [allRows]);
  const range = useMemo(() => periodRange(period, now, { from, to }), [from, now, period, to]);

  /** Tudo filtrado menos a visão: é a base dos contadores e da lista. */
  const withoutView = useMemo(
    () =>
      filterRows({
        rows: base,
        view: "todos",
        query,
        funnelId,
        priority,
        heatBand,
        period,
        from,
        to,
        quietDays,
        removedWithinDays,
        minValue,
        maxValue,
        now,
      }),
    [base, from, funnelId, heatBand, maxValue, minValue, now, period, priority, query, quietDays, removedWithinDays, to],
  );

  const selection = useMemo(
    () => withoutView.filter((row) => matchesView(row, view)),
    [view, withoutView],
  );
  const sorted = useMemo(
    () => sortRows(selection, sortKey, sortDirection),
    [selection, sortDirection, sortKey],
  );
  const pages = pageCount(sorted.length, pageSize);
  const page = Math.min(Math.max(1, requestedPage), pages);
  const visible = useMemo(() => paginate(sorted, page, pageSize), [page, pageSize, sorted]);

  const summary = useMemo(() => summarize(selection, scope), [scope, selection]);
  const activeFilters =
    (funnelId ? 1 : 0) +
    (priority ? 1 : 0) +
    (heatBand !== "todos" ? 1 : 0) +
    (scope !== "lixeira" && period !== DEFAULT_PERIOD ? 1 : 0) +
    (quietDays ? 1 : 0) +
    (removedWithinDays ? 1 : 0) +
    (minValue !== null ? 1 : 0) +
    (maxValue !== null ? 1 : 0) +
    (query ? 1 : 0);

  const clearFilters = () =>
    update({
      q: null,
      funil: null,
      prioridade: null,
      calor: null,
      periodo: null,
      de: null,
      ate: null,
      quieto: null,
      remocao: null,
      min: null,
      max: null,
    });

  // --- visões salvas --------------------------------------------------------

  const savedViews = store.savedViews;

  function applySavedView(saved?: SavedView) {
    if (!saved) return;
    update({
      visao: saved.view ?? null,
      q: saved.query || null,
      funil: saved.funnelId ?? null,
      prioridade: saved.priority ?? null,
      calor: saved.heatBand ?? null,
      periodo: null,
      de: null,
      ate: null,
      min: saved.minValue !== undefined ? String(saved.minValue) : null,
      max: saved.maxValue !== undefined ? String(saved.maxValue) : null,
      ordem: saved.sorting?.[0]?.id ?? null,
      dir: saved.sorting?.[0] ? (saved.sorting[0].desc ? "desc" : "asc") : null,
    });
    if (saved.columns?.length) {
      setColumnOrder(saved.columns);
      setHiddenColumns(
        columnsFor(scope)
          .map((column) => column.id)
          .filter((id) => !saved.columns!.includes(id)),
      );
    }
  }

  // --- colunas --------------------------------------------------------------

  const visibleColumns = useMemo(
    () => orderColumns(columnsFor(scope), columnOrder, hiddenColumns),
    [columnOrder, hiddenColumns, scope],
  );

  const onSort = (key: SortKey) => {
    if (key === sortKey) {
      update({ dir: sortDirection === "asc" ? "desc" : "asc", ordem: key });
      return;
    }
    update({ ordem: key, dir: key === "name" || key === "source" || key === "city" ? "asc" : "desc" });
  };

  // --- ações do domínio -----------------------------------------------------

  const openLead = (row: LeadRow) => navigate(`${appPath("leads")}/${row.lead.id}`);
  const messageLead = (row: LeadRow) => navigate(`${appPath("mensagens")}?lead=${row.lead.id}`);

  const peek = peekId ? allRows.find((row) => row.lead.id === peekId) : undefined;
  const from1 = sorted.length ? (page - 1) * pageSize + 1 : 0;
  const to1 = Math.min(page * pageSize, sorted.length);

  const trash = scope === "lixeira";
  const client = scope === "clientes";
  const entryDates = selection.map((row) => (client ? row.lead.clientSince : row.lead.createdAt));
  const windowDays = range
    ? Math.max(1, Math.round((Date.parse(`${range.to}T00:00:00Z`) - Date.parse(`${range.from}T00:00:00Z`)) / 86_400_000) + 1)
    : 30;
  const entry = growth(entryDates, now, undefined, windowDays, range?.to);
  const priceBound = Math.max(2500, ...base.map((row) => row.lead.potentialValue || 0));

  // --- cabeçalho ------------------------------------------------------------

  const sub = trash ? (
    <>
      Seus dados continuam aqui: <strong>{selection.length} {selection.length === 1 ? "lead preservado" : "leads preservados"}</strong>{" "}
      nesta seleção. Restaure um cadastro para retomar o acompanhamento.
    </>
  ) : client ? (
    <>
      Sua carteira segue em movimento: <strong>{entry.current} {entry.current === 1 ? "novo cliente" : "novos clientes"}</strong>{" "}
      {periodPhrase(period)}. Nesta seleção, <strong>{summary.attention} {summary.attention === 1 ? "precisa" : "precisam"} de atenção</strong>.
    </>
  ) : (
    <>
      Sua próxima oportunidade está aqui: <strong>{entry.current} {entry.current === 1 ? "novo lead" : "novos leads"}</strong>{" "}
      {periodPhrase(period)} e <strong>{summary.active} {summary.active === 1 ? "negociação em andamento" : "negociações em andamento"}</strong>{" "}
      nesta seleção. <strong>{summary.attention} {summary.attention === 1 ? "precisa" : "precisam"} de atenção</strong>.
    </>
  );

  const periodChips = PERIOD_OPTIONS.filter((option) => option.id !== "todos");

  // --- navegação lateral ----------------------------------------------------

  const countOf = (id: string) => withoutView.filter((row) => matchesView(row, id as ViewId)).length;
  /** Na lixeira, a lateral continua mostrando as visões de Leads, como no protótipo. */
  const sideViews = trash ? LEAD_VIEWS : views;
  const sideCounts = trash
    ? (id: string) => leadsBase.filter((row) => matchesView(row, id as ViewId)).length
    : countOf;

  const sidebar = (
    <aside className="z-lead-sidebar">
      <nav className="z-lead-nav" aria-label={`Visões e listas de ${meta.nounPlural}`}>
        {sideViews.map((item) => {
          const active = !trash && item.id === view;
          return (
            <button
              key={item.id}
              type="button"
              className={active ? "active" : undefined}
              aria-current={active ? "page" : undefined}
              onClick={() => {
                if (trash) {
                  navigate(`${appPath("leads")}${item.id === "todos" ? "" : `?visao=${item.id}`}`);
                  return;
                }
                update({ visao: item.id === views[0].id ? null : item.id });
              }}
            >
              <NavIcon id={item.id} />
              <span>{item.label}</span>
              <span className="cnt">{sideCounts(item.id)}</span>
            </button>
          );
        })}
        {scope === "leads" && savedViews.length > 0 && (
          <>
            <hr className="z-nav-divider" />
            {savedViews.map((saved) => (
              <span key={saved.id} className="z-saved-view-row">
                <button
                  type="button"
                  className="z-saved-view"
                  title={saved.name}
                  onClick={() => applySavedView(saved)}
                >
                  <NavIcon id="salva" />
                  <span>{saved.name}</span>
                </button>
                {/* Salvar sem excluir faz a trilha só crescer; §19.1 exige que
                    as duas pontas persistam no mesmo repositório. */}
                <IconButton
                  size="compact"
                  variant="bare"
                  label={`Excluir visão ${saved.name}`}
                  icon={<X size={13} aria-hidden="true" />}
                  onClick={() => setRemovendoVisao(saved)}
                />
              </span>
            ))}
          </>
        )}
        <hr className="z-nav-divider" />
        <button
          type="button"
          className={trash ? "active" : undefined}
          aria-current={trash ? "page" : undefined}
          onClick={() => {
            if (!trash) navigate(appPath("lixeira"));
          }}
        >
          <NavIcon id="lixeira" />
          <span>Lixeira</span>
          <span className="cnt">{scopeRows(allRows, "lixeira").length}</span>
        </button>
      </nav>
    </aside>
  );

  // --- tabela ---------------------------------------------------------------

  const sortLabel = (label: string, key?: SortKey) => {
    if (!key) return label;
    if (key !== sortKey) return `Ordenar por ${label}`;
    return `${label}, em ordem ${sortDirection === "asc" ? "crescente" : "decrescente"}; inverter`;
  };

  const cell = (column: (typeof visibleColumns)[number], row: LeadRow): ReactNode => {
    switch (column.id) {
      case "lead":
        return (
          <div className="lead-id">
            <span className="lead-ava" style={{ background: avatarGradient(row.lead.name) }} aria-hidden="true">
              {row.lead.photoUrl ? <img src={row.lead.photoUrl} alt="" /> : avatarInitials(row.lead.name)}
            </span>
            <div>
              <div className="nm">{row.lead.name}</div>
              <div className="co">{row.lead.company}</div>
            </div>
          </div>
        );
      case "stage":
        return (
          <>
            <span
              className="zn-stage-pill"
              style={
                {
                  "--cx-dot":
                    row.status === "won"
                      ? "var(--good)"
                      : row.status === "lost"
                        ? "var(--crit)"
                        : paletteColor(row.stageColor),
                } as CSSProperties
              }
            >
              <i aria-hidden="true" />
              {row.statusLabel}
            </span>
            <span className="z-lead-funnel">{row.funnelName}</span>
          </>
        );
      case "value":
        return money(row.lead.potentialValue || 0);
      case "heat":
        return (
          <span className="score">
            <span className="score-bar" aria-hidden="true">
              <i style={{ width: `${row.heat}%` }} />
            </span>
            <b className="num" style={{ color: heatColor(row.heat) }}>
              {row.heat}
            </b>
          </span>
        );
      case "source":
        return (
          <span className="src-chip" style={{ "--cx-dot": sourceColor(row.lead.source) } as CSSProperties}>
            <i aria-hidden="true" />
            {row.lead.source}
          </span>
        );
      case "priority":
        return (
          <span className={`prio ${priorityClass(row.lead.priority)}`}>
            <i aria-hidden="true" />
            {priorityLabel(row.lead.priority)}
          </span>
        );
      case "quiet":
        return <span className={row.quietDays >= 7 ? "last-int stale" : "last-int"}>{quietLabel(row)}</span>;
      case "city":
        return row.city || "—";
      case "removed":
        return row.lead.archivedAt ? removedStamp.format(new Date(row.lead.archivedAt)) : "Não informado";
      default:
        return null;
    }
  };

  const tdClass = (id: string) =>
    id === "value" ? "num" : id === "city" ? "z-city" : id === "removed" ? "z-muted" : undefined;

  const actions = (row: LeadRow): ReactNode =>
    trash ? (
      <div className="row-actions">
        <button
          type="button"
          className="zn-icon-btn"
          aria-label={`Restaurar ${row.lead.name}`}
          title="Restaurar"
          onClick={(event) => {
            event.stopPropagation();
            void store.restoreLead(row.lead.id);
          }}
        >
          <RotateCcw size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="zn-icon-btn z-danger"
          aria-label={`Excluir ${row.lead.name} permanentemente`}
          title="Excluir permanentemente"
          onClick={(event) => {
            event.stopPropagation();
            setRemoving(row.lead);
          }}
        >
          <Trash2 size={14} aria-hidden="true" />
        </button>
      </div>
    ) : (
      <div className="row-actions">
        <button
          type="button"
          className="zn-icon-btn"
          aria-label={`Mensagem para ${row.lead.name}`}
          title="Mensagem"
          onClick={(event) => {
            event.stopPropagation();
            messageLead(row);
          }}
        >
          <MessageCircle size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="zn-icon-btn"
          aria-label={`Enviar ${row.lead.name} para a lixeira`}
          title="Remover"
          onClick={(event) => {
            event.stopPropagation();
            void store.archiveLead(row.lead.id);
          }}
        >
          <Trash2 size={14} aria-hidden="true" />
        </button>
      </div>
    );

  const openPeek = (row: LeadRow) => {
    if (trash) return;
    setPeekId(row.lead.id);
  };

  const emptyState = (
    <div className="z-empty" role="status">
      {activeFilters || view !== views[0].id ? (
        <>
          <strong>Nada nesta seleção</strong>
          Os filtros atuais não alcançam nenhum cadastro.
          <div>
            <button type="button" className="btn-ghost" onClick={clearFilters}>
              Limpar filtros
            </button>
          </div>
        </>
      ) : (
        <>
          <strong>{trash ? "A lixeira está vazia" : `Nenhum ${meta.noun} ainda`}</strong>
          {trash
            ? "Cadastros removidos aparecem aqui até serem restaurados ou excluídos."
            : "Use “Novo lead”, no topo, para cadastrar o primeiro e começar a acompanhar a negociação."}
        </>
      )}
    </div>
  );

  return (
    <main className="z-list-identity" data-scope={scope}>
      <div className="pagehead">
        <div>
          <h1 ref={titleRef} tabIndex={-1}>
            {meta.title}
          </h1>
          <p className="sub">{sub}</p>
        </div>
        <div className="actions z-inline">
          {!trash && (
            <div className="chips-wrap z-list-period">
              <div
                className="chips"
                role="group"
                aria-label={client ? "Período de conversão dos clientes" : "Período de criação dos leads"}
                title={rangeLabel(range)}
              >
                {periodChips.map((option) => {
                  const active = option.id === period;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={active ? "active" : undefined}
                      aria-pressed={active}
                      aria-expanded={option.id === "custom" ? customOpen : undefined}
                      onClick={() => {
                        if (option.id === "custom") {
                          setCustomOpen((value) => !value);
                          return;
                        }
                        setCustomOpen(false);
                        update({ periodo: option.id === DEFAULT_PERIOD ? null : option.id, de: null, ate: null });
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              {customOpen && (
                <CustomPeriod
                  from={from}
                  to={to}
                  onApply={(nextFrom, nextTo) => {
                    update({ periodo: "custom", de: nextFrom, ate: nextTo });
                    setCustomOpen(false);
                  }}
                  onClose={() => setCustomOpen(false)}
                />
              )}
            </div>
          )}
          {!trash && (
            <button
              type="button"
              className="btn-primary"
              aria-label={`Exportar ${meta.nounPlural} filtrados em CSV`}
              disabled={sorted.length === 0}
              onClick={() => setExportOpen(true)}
            >
              <Download size={14} aria-hidden="true" />
              Exportar
            </button>
          )}
        </div>
      </div>

      {sidebar}

      <section className="z-lead-main" aria-label={trash ? "Leads na lixeira" : `Lista de ${meta.nounPlural}`}>
        <SummaryCards scope={scope} rows={selection} summary={summary} now={now} windowDays={windowDays} range={range} />

        <div className="z-lead-tools">
          <div className="search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              type="text"
              aria-label={meta.searchLabel}
              placeholder="Buscar nome, empresa, tag…"
              value={query}
              onChange={(event) => update({ q: event.target.value || null })}
            />
          </div>
          {!trash && (
            <button type="button" className="btn-ghost" onClick={() => setColumnsOpen(true)}>
              Colunas
            </button>
          )}
          <div className="sel z-price-filter">
            <button
              ref={priceButtonRef}
              type="button"
              className="btn-ghost"
              aria-haspopup="dialog"
              aria-expanded={priceOpen}
              aria-label={`Faixa de preço: ${
                minValue === null && maxValue === null
                  ? "Todos"
                  : `${money(minValue ?? 0)} a ${maxValue === null ? "sem limite" : money(maxValue)}`
              }`}
              onClick={() => setPriceOpen((value) => !value)}
            >
              Faixa de preço
            </button>
            {priceOpen && (
              <PricePopup
                anchorRef={priceButtonRef}
                bound={priceBound}
                min={minValue ?? 0}
                max={maxValue ?? priceBound}
                onChange={(nextMin, nextMax) =>
                  update({
                    min: nextMin > 0 ? String(nextMin) : null,
                    max: nextMax < priceBound ? String(nextMax) : null,
                  })
                }
                onClear={() => update({ min: null, max: null })}
                onClose={() => setPriceOpen(false)}
              />
            )}
          </div>
          <CompactSelect
            className="z-sel-score"
            prefix="AI Score"
            label="Faixa de AI Score"
            options={HEAT_BANDS.map((band) => ({ value: band.id, label: band.id === "todos" ? "Todos" : band.label }))}
            value={heatBand}
            onChange={(value) => update({ calor: value === "todos" ? null : value })}
          />
          {!trash && (
            <CompactSelect
              className="z-days-filter"
              prefix="Sem interação"
              options={QUIET_OPTIONS}
              value={String(quietDays)}
              onChange={(value) => update({ quieto: value === "0" ? null : value })}
            />
          )}
          <CompactSelect
            className="z-sel-funnel"
            prefix="Funil"
            options={[
              { value: "todos", label: "Todos" },
              ...store.funnels
                .filter((funnel) => !funnel.archived)
                .map((funnel) => ({ value: funnel.id, label: funnel.name })),
            ]}
            value={funnelId ?? "todos"}
            align="end"
            onChange={(value) => update({ funil: value === "todos" ? null : value })}
          />
          {trash && (
            <CompactSelect
              prefix="Remoção"
              label="Data de remoção"
              options={REMOVAL_OPTIONS}
              value={String(removedWithinDays)}
              onChange={(value) => update({ remocao: value === "0" ? null : value })}
            />
          )}
          <CompactSelect
            prefix="Por página"
            label="Itens por página"
            options={PAGE_SIZES.map((size) => ({ value: String(size), label: String(size) }))}
            value={String(pageSize)}
            align="end"
            onChange={(value) => update({ tamanho: value, pagina: null })}
          />
          {/* Só com filtro ativo: no estado inicial a barra é a do protótipo
              (busca, Colunas, Faixa de preço e os quatro seletores). */}
          {scope === "leads" && (activeFilters > 0 || view !== views[0].id) && (
            <button type="button" className="btn-ghost" onClick={() => setSavingView(true)}>
              Salvar visão
            </button>
          )}
          {activeFilters > 0 && (
            <button type="button" className="btn-ghost z-clear" onClick={clearFilters}>
              Limpar filtros
            </button>
          )}
        </div>

        {trash && (
          <nav className="subnav" aria-label="Filtrar leads na lixeira">
            {TRASH_VIEWS.map((item) => {
              const active = item.id === view;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={active ? "active" : undefined}
                  aria-pressed={active}
                  onClick={() => update({ visao: item.id === views[0].id ? null : item.id })}
                >
                  {item.label} <span className="cnt">{countOf(item.id)}</span>
                </button>
              );
            })}
          </nav>
        )}

        <section className="card table-card">
          <div
            className="table-scroll"
            tabIndex={0}
            role="region"
            aria-label={trash ? "Lista de leads na lixeira com rolagem" : `Lista de ${meta.nounPlural} com rolagem`}
          >
            <table
              className={trash ? "leads z-trash-table" : "leads"}
              aria-label={trash ? "Leads na lixeira" : `${meta.title} da visualização selecionada`}
            >
              <colgroup>
                {visibleColumns.map((column) => (
                  <col key={column.id} style={{ width: `${column.width}px` }} />
                ))}
                <col style={{ width: "96px" }} />
              </colgroup>
              <thead>
                <tr>
                  {visibleColumns.map((column) => {
                    const sorted = column.sort === sortKey;
                    return (
                      <th
                        key={column.id}
                        scope="col"
                        aria-sort={sorted ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}
                        className={sorted ? `sorted ${sortDirection}` : undefined}
                        style={column.id === "heat" ? { whiteSpace: "nowrap" } : undefined}
                      >
                        {column.sort ? (
                          <button
                            type="button"
                            className="z-lead-sort"
                            aria-label={sortLabel(column.label, column.sort)}
                            onClick={() => onSort(column.sort!)}
                          >
                            {column.label}
                          </button>
                        ) : (
                          column.label
                        )}
                        {column.id === "heat" && (
                          <HelpPopover about="o AI Score" label="O que é o AI Score" items={HEAT_EXPLANATION} />
                        )}
                      </th>
                    );
                  })}
                  <th scope="col">
                    <span className="z-visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr
                    key={row.lead.id}
                    tabIndex={trash ? undefined : 0}
                    aria-label={trash ? undefined : `Abrir ${row.lead.name}`}
                    aria-selected={row.lead.id === peekId || undefined}
                    onClick={() => openPeek(row)}
                    onKeyDown={(event) => {
                      if (trash) return;
                      if (event.target !== event.currentTarget) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openPeek(row);
                      }
                    }}
                  >
                    {visibleColumns.map((column) => (
                      <td key={column.id} className={tdClass(column.id)}>
                        {cell(column, row)}
                      </td>
                    ))}
                    <td>{actions(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {visible.length === 0 && emptyState}
          </div>
          <footer className="table-foot">
            <span role="status">
              {from1}–{to1} de <b>{sorted.length}</b> {meta.nounPlural}
            </span>
            <div className="z-lead-pagination">
              <span>{pageSize} por página</span>
              <div className="pages">
                <button
                  type="button"
                  disabled={page <= 1}
                  aria-label="Página anterior"
                  onClick={() => update({ pagina: String(page - 1) })}
                >
                  Anterior
                </button>
                <span>
                  {page} / {pages}
                </span>
                <button
                  type="button"
                  disabled={page >= pages}
                  aria-label="Próxima página"
                  onClick={() => update({ pagina: String(page + 1) })}
                >
                  Próxima
                </button>
              </div>
            </div>
          </footer>
        </section>
      </section>

      {columnsOpen && (
        <ColumnsDialog
          scope={scope}
          order={columnOrder}
          hidden={hiddenColumns}
          onClose={() => setColumnsOpen(false)}
          onApply={(order, hidden) => {
            setColumnOrder(order);
            setHiddenColumns(hidden);
            setColumnsOpen(false);
          }}
        />
      )}

      {savingView && (
        <SaveViewDialog
          onClose={() => setSavingView(false)}
          onSave={(name) => {
            store.addSavedView({
              schemaVersion: 2,
              name,
              query,
              funnelId: funnelId ?? undefined,
              priority: priority ?? undefined,
              view,
              heatBand,
              minValue: minValue ?? undefined,
              maxValue: maxValue ?? undefined,
              columns: visibleColumns.map((column) => column.id),
              sorting: [{ id: sortKey, desc: sortDirection === "desc" }],
            });
            setSavingView(false);
          }}
        />
      )}

      {exportOpen && (
        <ExportDialog
          open
          onClose={() => setExportOpen(false)}
          rows={sorted}
          noun={client ? "clientes" : "leads"}
          fileName={csvFileName(scope, now)}
        />
      )}

      {peek && (
        <LeadPeek
          row={peek}
          now={now}
          onClose={() => setPeekId(null)}
          onOpen={() => {
            setPeekId(null);
            openLead(peek);
          }}
          onMessage={() => {
            setPeekId(null);
            messageLead(peek);
          }}
        />
      )}

      {removing && (
        <PermanentDeleteDialog
          lead={removing}
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            await store.deleteLeadPermanently(removing.id);
            setRemoving(null);
          }}
        />
      )}
      {removendoVisao && (
        <ConfirmDialog
          title="Excluir visão"
          question={`"${removendoVisao.name}" sai da trilha. Os filtros continuam disponíveis; o que se perde é o atalho salvo.`}
          confirmLabel="Excluir visão"
          danger
          onConfirm={() => {
            store.deleteSavedView(removendoVisao.id);
            setRemovendoVisao(null);
            showFeedback("Visão excluída.", "success");
          }}
          onClose={() => setRemovendoVisao(null)}
        />
      )}
    </main>
  );
}

// --- indicadores ------------------------------------------------------------

/** Curva suave do protótipo (`spark`, 96×34) com preenchimento, linha e ponto final. */
function Spark({ points, color, label }: { points: number[]; color: string; label: string }) {
  const width = 96;
  const height = 34;
  const max = Math.max(...points, 0);
  const min = Math.min(...points, 0);
  const span = max - min || 1;
  const stepX = points.length > 1 ? (width - 6) / (points.length - 1) : 0;
  const coords = points.map((value, index) => ({
    x: 3 + stepX * index,
    y: height - 4 - ((value - min) / span) * (height - 10),
  }));
  let line = "";
  coords.forEach((point, index) => {
    if (index === 0) {
      line += `M${point.x},${point.y}`;
      return;
    }
    const prev = coords[index - 1];
    const dx = (point.x - prev.x) / 2;
    line += ` C${prev.x + dx},${prev.y} ${point.x - dx},${point.y} ${point.x},${point.y}`;
  });
  const last = coords[coords.length - 1];
  const fill = coords.length ? `${line} L${last.x},${height} L${coords[0].x},${height} Z` : "";
  return (
    <svg className="spark" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      <path className="sp-fill" d={fill} fill={color} opacity=".13" />
      <path className="sp-line" pathLength={1} d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      {last && <circle className="sp-dot" cx={last.x} cy={last.y} r="2.6" fill={color} />}
    </svg>
  );
}

function SummaryCards({
  scope,
  rows,
  summary,
  now,
  windowDays,
  range,
}: {
  scope: ListScope;
  rows: readonly LeadRow[];
  summary: ReturnType<typeof summarize>;
  now: string;
  windowDays: number;
  range: { from: string; to: string } | null;
}) {
  const client = scope === "clientes";
  const trash = scope === "lixeira";
  const end = range?.to;
  const entryDates = rows.map((row) => (client ? row.lead.clientSince : row.lead.createdAt));
  const entry = growth(entryDates, now, undefined, windowDays, end);
  const activeRows = client ? rows : rows.filter((row) => row.status === "active");
  const activeTrend = growth(
    activeRows.map((row) => (client ? row.lead.clientSince : row.lead.createdAt)),
    now,
    undefined,
    windowDays,
    end,
  );
  const attentionRows = (trash ? rows : activeRows).filter((row) => row.quietDays >= 7 || row.pendingTasks === 0);
  const removedTrend = growth(rows.map((row) => row.lead.archivedAt), now, undefined, 30);
  const attentionTrend = growth(attentionRows.map((row) => row.lead.createdAt), now, undefined, windowDays, end);
  const valueRows = trash ? rows : activeRows;
  const valueTrend = growth(
    valueRows.map((row) => row.lead.createdAt),
    now,
    valueRows.map((row) => row.lead.potentialValue || 0),
    windowDays,
    end,
  );

  const windowText = range
    ? `De ${rangeLabel(range).replace(" – ", " a ")}, comparado ao período anterior de igual duração.`
    : "Últimos 30 dias, comparados aos 30 dias anteriores.";
  const scopeHelp = `${windowText} Respeita os demais filtros e todas as páginas da lista.`;
  const compareHelp =
    "Comparação demonstrativa entre as coortes dos dois períodos, usando as situações e valores atuais; não reconstrói alterações históricas. Variação percentual em relação ao período anterior de igual duração.";

  type Card = {
    key: string;
    label: string;
    value: number | null;
    money: boolean;
    featured?: boolean;
    attention?: boolean;
    inverted?: boolean;
    comparison: ReturnType<typeof growth>;
    help: MetricExplanation[];
    unavailable?: string;
  };

  const cards: Card[] = trash
    ? [
        {
          key: "valor",
          label: "Valor dos registros",
          value: summary.value,
          money: true,
          featured: true,
          inverted: true,
          comparison: valueTrend,
          help: [
            { term: "Recorte", description: "Total preservado nesta seleção. Respeita todos os filtros e a visão desta lista; inclui todas as páginas." },
            { term: "Fórmula", description: "Soma dos valores cadastrais atuais, agrupados pela data de criação. Não é receita recebida nem LTV." },
            { term: "Leitura", description: "Na lixeira, aumentos representam perda e aparecem em vermelho; reduções aparecem em verde." },
          ],
        },
        {
          key: "na-lixeira",
          label: "Na lixeira",
          value: rows.length,
          money: false,
          inverted: true,
          comparison: entry,
          help: [
            { term: "Recorte", description: "Cadastros preservados nesta seleção, contados uma vez cada." },
            { term: "Rodapé", description: "Compara quantos dos cadastros ainda na lixeira foram criados neste período e no anterior." },
          ],
        },
        {
          key: "removidos",
          label: "Removidos em 30 dias",
          value: removedTrend.current,
          money: false,
          inverted: true,
          comparison: removedTrend,
          help: [
            { term: "Recorte", description: "Cadastros desta seleção enviados à lixeira nos últimos 30 dias, pela data da remoção. Removidos sem data conhecida não entram." },
            { term: "Comparação", description: "Contra os 30 dias anteriores; mais remoções são desfavoráveis." },
          ],
        },
        {
          key: "atencao",
          label: "Precisam de atenção",
          value: summary.attention,
          money: false,
          attention: true,
          inverted: true,
          comparison: attentionTrend,
          help: [
            { term: "Fórmula", description: "Cadastros preservados sem interação há sete dias ou sem tarefa pendente, contados uma única vez." },
          ],
        },
      ]
    : [
        {
          key: "novos",
          label: client ? "Novos clientes" : "Novos leads",
          value: entry.current,
          money: false,
          featured: true,
          comparison: entry,
          help: [
            { term: "Recorte", description: scopeHelp },
            {
              term: "Fórmula",
              description: client
                ? "A data considerada é a conversão. Clientes que entraram no período, contados uma vez por cadastro."
                : "A data considerada é a criação do lead. Cadastros que entraram no período, contados uma vez por cadastro.",
            },
            { term: "Comparação", description: compareHelp },
            { term: "Curva", description: "Seis intervalos iguais dentro do período, do mais antigo ao mais recente." },
          ],
        },
        {
          key: "ativos",
          label: client ? "Clientes na carteira" : "Em negociação",
          value: summary.active,
          money: false,
          comparison: activeTrend,
          help: [
            { term: "Recorte", description: scopeHelp },
            {
              term: "Fórmula",
              description: client
                ? "Quantidade atual de clientes na seleção."
                : "Cadastros atualmente em negociação: participações com situação ativa. Ganhas e perdidas ficam de fora.",
            },
            { term: "Comparação", description: compareHelp },
          ],
        },
        {
          key: "valor",
          label: client ? "Valor dos negócios" : "Valor em negociação",
          value: summary.value,
          money: true,
          comparison: valueTrend,
          help: [
            { term: "Recorte", description: scopeHelp },
            { term: "Fórmula", description: "Soma dos valores cadastrais atuais; não é receita recebida nem LTV." },
            { term: "Comparação", description: compareHelp },
          ],
        },
        {
          key: "atencao",
          label: "Precisam de atenção",
          value: summary.attention,
          money: false,
          attention: true,
          inverted: true,
          comparison: attentionTrend,
          help: [
            { term: "Recorte", description: scopeHelp },
            {
              term: "Fórmula",
              description:
                "Cadastros atualmente sem interação há sete dias ou sem tarefa pendente, contados uma única vez. Mais ocorrências são desfavoráveis.",
            },
            { term: "Interação real", description: "Contato efetivo com o lead. Editar cadastro, mover etapa ou criar nota não contam." },
            { term: "Comparação", description: compareHelp },
          ],
        },
      ];

  return (
    <dl
      className="z-lead-summary z-growth-grid"
      aria-label={trash ? "Resumo da lixeira filtrada" : client ? "Resumo dos clientes filtrados" : "Resumo da base filtrada"}
    >
      {cards.map((card) => {
        const diff = card.comparison.current - card.comparison.before;
        const favorable = card.inverted ? diff <= 0 : diff >= 0;
        const format = (value: number) => (card.money ? money(value) : value.toLocaleString("pt-BR"));
        const tone = card.featured ? "var(--grad-ink)" : diff === 0 ? "var(--muted)" : favorable ? "var(--good)" : "var(--crit)";
        return (
          <div
            key={card.key}
            className={[
              "z-growth-card",
              "kpi",
              trash ? "z-trash-metric" : "",
              card.featured ? "kpi-featured" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={trash ? ({ "--loss-chart": card.featured ? "#FFD0D0" : "var(--crit)" } as CSSProperties) : undefined}
          >
            <dt className="label">
              {card.label}
              <HelpPopover about={card.label.toLowerCase()} label={`Como é calculado: ${card.label}`} items={card.help} align="end" />
            </dt>
            <dd>
              <div className="z-growth-body">
                <div>
                  <strong
                    className={[
                      "num",
                      "z-growth-number",
                      card.attention ? "z-attention-value" : "",
                      card.value === null ? "z-unavailable" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    title={card.unavailable}
                  >
                    {card.value === null ? "—" : format(card.value)}
                  </strong>
                </div>
              </div>
              <div className="z-growth-foot">
                {card.value === null ? (
                  <span className="delta flat">sem registro</span>
                ) : (
                  <span className={`delta ${diff === 0 ? "flat" : favorable ? "up" : "down"}`}>
                    {deltaLabel(card.comparison, format)}
                  </span>
                )}
                {card.value !== null && (
                  <Spark
                    points={card.comparison.bins}
                    color={tone}
                    label={`${scopeHelp} Valores por intervalo: ${card.comparison.bins.map(format).join(", ")}`}
                  />
                )}
              </div>
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

// --- diálogos ---------------------------------------------------------------

function ColumnsDialog({
  scope,
  order,
  hidden,
  onClose,
  onApply,
}: {
  scope: ListScope;
  order: string[];
  hidden: string[];
  onClose: () => void;
  onApply: (order: string[], hidden: string[]) => void;
}) {
  const all = columnsFor(scope);
  const [draftOrder, setDraftOrder] = useState<string[]>(() => {
    const known = all.map((column) => column.id);
    return [...order.filter((id) => known.includes(id)), ...known.filter((id) => !order.includes(id))];
  });
  const [draftHidden, setDraftHidden] = useState<string[]>(hidden);

  const move = (position: number, delta: number) => {
    setDraftOrder((current) => {
      const next = [...current];
      const target = position + delta;
      if (target < 0 || target >= next.length) return current;
      [next[position], next[target]] = [next[target], next[position]];
      return next;
    });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Colunas da lista"
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={() => onApply(draftOrder, draftHidden)}>
            Aplicar
          </button>
        </>
      }
    >
      <p className="z-help-text">
        Escolha o que aparece e use as setas para organizar. Identificação e ações permanecem disponíveis.
      </p>
      <ul className="z-column-list">
        {draftOrder.map((id, position) => {
          const column = all.find((item) => item.id === id);
          if (!column) return null;
          const locked = LOCKED_COLUMNS.includes(id);
          return (
            <li className="z-column-row" key={id}>
              <label>
                <input
                  type="checkbox"
                  checked={!draftHidden.includes(id)}
                  disabled={locked}
                  onChange={(event) =>
                    setDraftHidden((current) =>
                      event.target.checked ? current.filter((item) => item !== id) : [...current, id],
                    )
                  }
                />
                {column.label}
                {locked && <span className="cx-sr-only">Sempre visível</span>}
              </label>
              <button
                type="button"
                className="btn-ghost"
                aria-label={`Mover ${column.label} para a esquerda`}
                disabled={position === 0}
                onClick={() => move(position, -1)}
              >
                ←
              </button>
              <button
                type="button"
                className="btn-ghost"
                aria-label={`Mover ${column.label} para a direita`}
                disabled={position === draftOrder.length - 1}
                onClick={() => move(position, 1)}
              >
                →
              </button>
            </li>
          );
        })}
      </ul>
    </Dialog>
  );
}

function SaveViewDialog({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState("");
  return (
    <Dialog
      open
      onClose={onClose}
      dirty={name.trim().length > 0}
      title="Salvar visão"
      description="Guarda a visão, a busca, os filtros, as colunas visíveis, a ordem das colunas e a ordenação."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={!name.trim()} onClick={() => onSave(name.trim())}>
            Salvar visão
          </button>
        </>
      }
    >
      <Field label="Nome da visão" required>
        {({ id }) => (
          <TextInput id={id} value={name} autoFocus onChange={(event) => setName(event.target.value)} />
        )}
      </Field>
    </Dialog>
  );
}

function PermanentDeleteDialog({
  lead,
  onClose,
  onConfirm,
}: {
  lead: Lead;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [typed, setTyped] = useState("");
  const [working, setWorking] = useState(false);
  const matches = typed.trim().toLowerCase() === lead.name.trim().toLowerCase();

  return (
    <Dialog
      open
      onClose={onClose}
      title="Excluir permanentemente"
      description="A operação apaga o cadastro, a participação, as tarefas, a jornada, os anexos e os vínculos. Não há como desfazer."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <Button
            variant="danger"
            disabled={!matches}
            loading={working}
            onClick={async () => {
              setWorking(true);
              try {
                await onConfirm();
              } finally {
                setWorking(false);
              }
            }}
          >
            Excluir permanentemente
          </Button>
        </>
      }
    >
      <Field
        label={`Digite ${lead.name} para confirmar`}
        required
        help="A confirmação nominal existe para que uma exclusão irreversível nunca aconteça por um clique distraído."
      >
        {({ id }) => (
          <TextInput id={id} value={typed} autoFocus onChange={(event) => setTyped(event.target.value)} />
        )}
      </Field>
    </Dialog>
  );
}

// --- acesso rápido ----------------------------------------------------------

const EVENT_TONE: Record<TimelineTone, string> = {
  stage: "var(--accent)",
  note: "var(--muted)",
  task: "var(--accent-2)",
  message: "var(--accent)",
  file: "var(--muted)",
  outcome: "var(--good)",
  finance: "var(--good)",
  meeting: "var(--warn)",
  other: "var(--muted)",
};

function EventIcon({ tone }: { tone: TimelineTone }) {
  switch (tone) {
    case "task":
      return <CheckCircle2 size={13} aria-hidden="true" />;
    case "note":
      return <StickyNote size={13} aria-hidden="true" />;
    case "message":
      return <MessageCircle size={13} aria-hidden="true" />;
    case "outcome":
    case "finance":
      return <CheckCircle2 size={13} aria-hidden="true" />;
    case "meeting":
      return <Clock3 size={13} aria-hidden="true" />;
    default:
      return <FileText size={13} aria-hidden="true" />;
  }
}

/** "16 de set. de 2026, 14:02" */
const removedStamp = new Intl.DateTimeFormat("pt-BR", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "America/Sao_Paulo",
});

const stampFormat = new Intl.DateTimeFormat("pt-BR", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "America/Sao_Paulo",
});

/**
 * Acesso rápido de um lead aberto de outra superfície — a Inbox e as
 * conversas —, igual ao da lista: é ele que o "Ver contato" do protótipo
 * abre. O perfil inteiro só abre por "Abrir perfil completo".
 */
export function LeadQuickPeek({ leadId, onClose }: { leadId: string; onClose: () => void }) {
  const store = useStore();
  const navigate = useNavigate();
  const now = useMemo(() => new Date().toISOString(), []);
  const row = useMemo(
    () =>
      buildRows({
        leads: store.leads.filter((lead) => lead.id === leadId),
        memberships: store.memberships,
        stages: store.stages,
        funnels: store.funnels,
        tags: store.tags,
        tasks: store.tasks,
        leadFields: store.leadFields,
        leadFieldValues: store.leadFieldValues,
        now,
      })[0],
    [leadId, now, store.funnels, store.leadFieldValues, store.leadFields, store.leads, store.memberships, store.stages, store.tags, store.tasks],
  );
  if (!row) return null;
  return (
    <LeadPeek
      row={row}
      now={now}
      onClose={onClose}
      onOpen={() => {
        onClose();
        navigate(`${appPath("leads")}/${leadId}`);
      }}
      onMessage={() => {
        onClose();
        navigate(`${appPath("mensagens")}?lead=${leadId}`);
      }}
    />
  );
}

function LeadPeek({
  row,
  now,
  onClose,
  onOpen,
  onMessage,
}: {
  row: LeadRow;
  now: string;
  onClose: () => void;
  onOpen: () => void;
  onMessage: () => void;
}) {
  const store = useStore();
  const ref = useRef<HTMLElement>(null);
  const titleId = useId();
  const [taskOpen, setTaskOpen] = useState(false);
  useModalOverlay({ containerRef: ref, onClose, active: !taskOpen });

  const lead = row.lead;
  const stages = useMemo(
    () => store.stages.filter((stage) => stage.funnelId === row.funnelId && !stage.archived).sort((a, b) => a.order - b.order),
    [row.funnelId, store.stages],
  );
  const journey = useMemo(
    () => stageJourney({ stages, membership: row.membership, events: store.events, now }),
    [now, row.membership, stages, store.events],
  );
  const entries = useMemo(() => timeline(store.events, lead.id).slice(0, 5), [lead.id, store.events]);
  const state = situation({
    status: row.membership?.status,
    quietDays: lead.lastInteractionAt ? row.quietDays : null,
    pendingTasks: row.pendingTasks,
    isClient: row.isClient,
  });
  const signal =
    state.tone === "good" ? "var(--good)" : state.tone === "warn" ? "var(--warn)" : state.tone === "crit" ? "var(--crit)" : "var(--accent)";
  const stageTone =
    row.status === "won"
      ? "var(--good)"
      : row.status === "lost"
        ? "var(--crit)"
        : paletteColor(row.stageColor);
  const currentIndex = journey.findIndex((step) => step.state === "current");
  const contextText = [
    row.statusLabel === "Cliente" ? "Negociação ganha" : row.status === "lost" ? "Negociação perdida" : row.stageName,
    lead.lastInteractionAt ? `Sem interação há ${row.quietDays} ${row.quietDays === 1 ? "dia" : "dias"}` : "Sem interação registrada",
  ].join(" · ");

  return (
    <>
      <div className="zn-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
        <aside
          ref={ref}
          className="peek z-peek-overview"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          style={{ "--peek-stage": stageTone } as CSSProperties}
        >
          <div className="peek-head">
            <span className="lead-ava" style={{ background: avatarGradient(lead.name) }} aria-hidden="true">
              {lead.photoUrl ? <img src={lead.photoUrl} alt="" /> : avatarInitials(lead.name)}
            </span>
            <div className="ph-info">
              <h2 id={titleId}>{lead.name}</h2>
              <div className="ph-co">
                <strong>{lead.company}</strong>
                <span>{[lead.role, row.city].filter(Boolean).join(" · ")}</span>
              </div>
              <div className="ph-meta">
                <span className="zn-stage-pill" style={{ "--cx-dot": stageTone } as CSSProperties}>
                  <i aria-hidden="true" />
                  {row.statusLabel === "Cliente" || row.status === "lost" ? row.stageName : row.statusLabel}
                </span>
                <span className={`prio ${priorityClass(lead.priority)}`}>
                  <i aria-hidden="true" />
                  {priorityLabel(lead.priority)}
                </span>
                {row.tagNames.map((tag) => (
                  <span className="ktag" key={tag}>
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <button type="button" className="zn-icon-btn" aria-label="Fechar" title="Fechar" onClick={onClose}>
              <X size={16} aria-hidden="true" />
            </button>
          </div>

          <div className="peek-body">
            <section
              className="z-peek-diagnosis"
              aria-label="Diagnóstico e sugestão"
              style={{ "--peek-signal": signal } as CSSProperties}
            >
              <span className="z-peek-signal-icon" aria-hidden="true">
                <Clock3 size={17} />
              </span>
              <div>
                <h3>{state.title}</h3>
                <p className="z-peek-context">{contextText}</p>
                <p>{state.text}</p>
              </div>
              <HelpPopover
                about="a situação"
                label="Sobre a situação e a próxima ação"
                align="end"
                items={[
                  {
                    term: "Como é derivada",
                    description:
                      "Situação derivada do cadastro e do tempo informado desde a última interação. A sugestão não representa uma tarefa criada nem uma ação executada. Não é uma análise de IA.",
                  },
                ]}
              />
            </section>

            <div className="peek-kpis">
              <div className="peek-kpi kpi-featured">
                <div className="l">
                  Valor potencial
                  <HelpPopover
                    about="o valor"
                    label="Sobre o valor"
                    align="end"
                    items={[{ term: "Valor potencial", description: "Valor cadastral desta negociação. Não corresponde à receita recebida nem ao LTV." }]}
                  />
                </div>
                <div className="v num">{money(lead.potentialValue || 0)}</div>
              </div>
              <div
                className="peek-kpi"
                style={{ "--peek-score": heatColor(row.heat), "--peek-score-width": `${row.heat}%` } as CSSProperties}
              >
                <div className="l">
                  AI Score
                  <HelpPopover about="o AI Score" label="O que é o AI Score" align="end" items={HEAT_EXPLANATION} />
                </div>
                <div className="v z-peek-score">
                  <span className="z-peek-score-value">{row.heat}</span>
                  <span className="z-peek-score-meter" aria-hidden="true">
                    <i />
                  </span>
                </div>
              </div>
            </div>

            <dl className="z-peek-facts">
              <div className="z-peek-fact" style={{ "--peek-fact-color": sourceColor(lead.source) } as CSSProperties}>
                <Flag aria-hidden="true" />
                <div>
                  <dt>Origem</dt>
                  <dd>{lead.source}</dd>
                </div>
              </div>
              <div className="z-peek-fact" style={{ "--peek-fact-color": "var(--accent-2)" } as CSSProperties}>
                <Clock3 aria-hidden="true" />
                <div>
                  <dt>No funil há</dt>
                  <dd className="num">
                    {daysInFunnel(lead, now)} {daysInFunnel(lead, now) === 1 ? "dia" : "dias"}
                  </dd>
                </div>
              </div>
            </dl>

            <div className="z-peek-section">
              <h3>Jornada no funil</h3>
              <span>
                {currentIndex >= 0 ? `${currentIndex + 1} de ${journey.length} etapas` : `${journey.length} etapas`}
              </span>
            </div>
            <p className="z-peek-funnel-name">{row.funnelName}</p>
            <div className="zn-journey" role="list" aria-label="Etapas do funil">
              {journey.map((step) => (
                <div
                  key={step.stage.id}
                  role="listitem"
                  className={`j-step ${step.state === "past" ? "past" : step.state === "current" ? "cur" : ""}`}
                  style={{ "--peek-step": paletteColor(step.stage.color) } as CSSProperties}
                  aria-current={step.state === "current" ? "step" : undefined}
                >
                  <span className="j-dot" aria-hidden="true" />
                  <div className="j-name">{step.stage.name}</div>
                  <div className="j-time num">{step.durationLabel ?? (step.state === "future" ? "—" : "")}</div>
                </div>
              ))}
            </div>

            <div className="z-peek-section">
              <h3>Últimas atividades</h3>
              <span>
                {entries.length} {entries.length === 1 ? "registro" : "registros"}
              </span>
            </div>
            {entries.length === 0 ? (
              <p className="z-help-text">Nenhum registro na jornada ainda.</p>
            ) : (
              <div className="tl z-peek-timeline">
                {entries.map((entry) => (
                  <div
                    key={entry.event.id}
                    className="z-peek-event"
                    style={{ "--peek-event": EVENT_TONE[entry.tone] } as CSSProperties}
                  >
                    <span className="z-peek-event-icon" aria-hidden="true">
                      <EventIcon tone={entry.tone} />
                    </span>
                    <div className="tt">{entry.event.title}</div>
                    <time dateTime={entry.event.occurredAt}>
                      <span className="z-peek-event-type">{entry.label}</span>
                      {stampFormat.format(new Date(entry.event.occurredAt)).replace(",", " ·")}
                    </time>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="peek-foot">
            <button type="button" className="btn-ghost" onClick={() => setTaskOpen(true)}>
              + Tarefa
            </button>
            <button type="button" className="btn-ghost" onClick={onMessage}>
              Mensagem
            </button>
            <button type="button" className="btn-primary" onClick={onOpen}>
              Abrir perfil completo
            </button>
          </div>
        </aside>
      </div>
      {taskOpen && <NewTaskDialog open progress="todo" fixedLeadId={lead.id} onClose={() => setTaskOpen(false)} />}
    </>
  );
}
